// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  type DatabaseField,
  DatabaseFieldType,
  type EntryWithContext,
  type FieldWithContext,
  type ValueWithContext,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';

import { RECURSION_LIMIT } from '../../constants';
import { loadDatabaseByContentId } from '../../redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByIdOptional,
} from '../../redux/databases/selectors';
import {
  type FieldType,
  getNumberRepresentation,
  getStringRepresentation,
  isEmptyValue,
  isEqualValueList,
  loadDependencies,
  renderExportView,
} from '../field-types';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getDatabaseValue,
  isBrokenDatabase,
} from '../utils';

import { getBacklinkEntries } from './backlink';
import { getEntryLinkTargetEntries } from './entryLink';

/**
 * Returns the entry link / backlink field in the same database that the lookup field references.
 */
export function getReferencedEntryDetailsField({
  database,
  field,
}: FieldWithContext<DatabaseFieldType, ObjectMode>):
  | DatabaseField<DatabaseFieldType.ENTRY_LINK>
  | DatabaseField<DatabaseFieldType.BACKLINK>
  | undefined {
  const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
  if (
    referencedField?.type !== DatabaseFieldType.ENTRY_LINK &&
    referencedField?.type !== DatabaseFieldType.BACKLINK
  ) {
    return undefined;
  } else {
    return referencedField as
      | DatabaseField<DatabaseFieldType.ENTRY_LINK>
      | DatabaseField<DatabaseFieldType.BACKLINK>;
  }
}

/**
 * Returns the entries in the target database that the referenced entry link / backlink field references.
 */
export function getReferencedEntryDetailsEntries<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> & EntryWithContext<ObjectMode>,
  state: State,
  {
    selectDatabase = selectDatabaseByContentIdOptional,
    recursionCount = 0,
  }: {
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    recursionCount?: number;
  } = {}
): EntryWithContext[] {
  const referencedField = getReferencedEntryDetailsField({ database, field });

  if (referencedField?.type === DatabaseFieldType.ENTRY_LINK) {
    const referencedValue = getDatabaseValue(database, referencedField, entry);

    if (referencedValue.type === DatabaseFieldType.ENTRY_LINK) {
      return getEntryLinkTargetEntries(
        { field: referencedField, value: referencedValue.value },
        state,
        { selectDatabase }
      );
    }

    return [];
  } else if (referencedField?.type === DatabaseFieldType.BACKLINK) {
    return getBacklinkEntries({ database, field: referencedField, entry }, state, {
      selectDatabase,
      recursionCount: recursionCount + 1,
    });
  } else {
    return [];
  }
}

/**
 * Returns the values (with context) from the target database that are referenced by the referenced entry field. Multiple values
 * can be returned if the entry field is a multi field.
 */
export function getEntryDetailsTargetValues<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> &
    Optional<EntryWithContext<ObjectMode>, 'entry'>,
  state: State | undefined,
  {
    selectDatabase = selectDatabaseByContentIdOptional,
    recursionCount = 0,
  }: {
    selectDatabase?: typeof selectDatabaseByContentIdOptional;
    recursionCount?: number;
  } = {}
): ValueWithContext[] {
  if (!entry || !state) {
    return [];
  }

  const referencedField = getReferencedEntryDetailsField({ database, field });
  if (
    !referencedField ||
    (!referencedField.settings.contentId &&
      (!referencedField.settings.databaseSpace || !referencedField.settings.databaseId))
  ) {
    return [];
  }

  let _db = undefined;
  if (referencedField.settings.databaseId && referencedField.settings.databaseSpace) {
    _db = selectDatabaseByIdOptional(state, {
      spaceKey: referencedField.settings.databaseSpace.key,
      id: referencedField.settings.databaseId,
    });
  } else if (referencedField.settings.contentId) {
    _db = selectDatabaseByContentIdOptional(state, {
      contentId: referencedField.settings.contentId,
    });
  }

  const targetDatabase = _db && selectDatabase(state, { contentId: _db.contentId });
  const targetField =
    targetDatabase &&
    !isBrokenDatabase(targetDatabase) &&
    getDatabaseFieldOptional(targetDatabase, field.settings.lookupFieldId);
  if (!targetField) {
    return [];
  }

  return getReferencedEntryDetailsEntries({ database, field, entry }, state, {
    selectDatabase,
    recursionCount,
  }).map((targetEntry) => {
    const targetValue = getDatabaseValue(
      targetEntry.database,
      targetField,
      targetEntry.entry
    );
    return { ...targetEntry, field: targetField, value: targetValue };
  });
}

const entryDetailsFieldType: FieldType<DatabaseFieldType.ENTRY_DETAILS> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  isCsvImportSupported: false,

  renderExportView: (
    { database, field, entry },
    state,
    renderContext,
    recursionCount,
    contentId
  ) => {
    if (recursionCount >= RECURSION_LIMIT) {
      return '';
    }

    return getEntryDetailsTargetValues({ database, field, entry }, state, {
      recursionCount,
    })
      .map((context) =>
        renderExportView(context, state, renderContext, recursionCount + 1, contentId)
      )
      .join('<br/>');
  },

  getInitialValue: () => undefined,

  isEmptyValue: ({ database, field, entry }, state, recursionCount) => {
    const targetValues = getEntryDetailsTargetValues({ database, field, entry }, state, {
      recursionCount,
    });
    return targetValues.every((context) =>
      isEmptyValue(context, state, recursionCount + 1)
    );
  },

  isEqualValue: (context1, context2, state, recursionCount) => {
    const targetValues1 = getEntryDetailsTargetValues(context1, state, {
      recursionCount,
    });
    const targetValues2 = getEntryDetailsTargetValues(context2, state, {
      recursionCount,
    });
    return isEqualValueList(targetValues1, targetValues2, state, recursionCount + 1);
  },

  getStringRepresentation: ({ database, field, entry }, state, { recursionCount }) => {
    if (!state || recursionCount >= RECURSION_LIMIT) {
      return '';
    }

    return getEntryDetailsTargetValues({ database, field, entry }, state, {
      recursionCount,
    })
      .map((context) =>
        getStringRepresentation(context, state, { recursionCount: recursionCount + 1 })
      )
      .join('\n');
  },

  getNumberRepresentation: ({ database, field, entry }, state, recursionCount) => {
    if (!state || recursionCount >= RECURSION_LIMIT) {
      return undefined;
    }

    const values = getEntryDetailsTargetValues({ database, field, entry }, state, {
      recursionCount,
    });
    if (values.length === 1) {
      return getNumberRepresentation(values[0], state, recursionCount + 1);
    }
  },

  loadDependencies: (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase, helpers },
    state,
    { recursionCount }
  ) => {
    if (recursionCount >= RECURSION_LIMIT) {
      return;
    }

    const referencedField = selector((state) => {
      const database = selectDatabase(state, { contentId });
      return database && !isBrokenDatabase(database)
        ? getReferencedEntryDetailsField({ database, field })
        : undefined;
    });

    if (referencedField && referencedField.settings.contentId) {
      dispatch(loadDatabaseByContentId(referencedField.settings.contentId));

      const targetValues = selector((state) => {
        const _db = selectDatabase(state, { contentId });
        const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
        const entry =
          database && entryId ? getDatabaseEntryOptional(database, entryId) : undefined;
        return (
          database &&
          entry &&
          getEntryDetailsTargetValues({ database, field, entry }, state, {
            recursionCount,
            selectDatabase,
          }).map((v) => ({
            contentId: v.database.contentId,
            field: v.field,
            entryId: v.entry.id,
            value: v.value,
          }))
        );
      });
      if (targetValues) {
        for (const context of targetValues) {
          loadDependencies(context, helpers, state, {
            recursionCount: recursionCount + 1,
          });
        }
      }
    }
  },

  noDefaultValue: true,
};

export default entryDetailsFieldType;
